/**
 * Hook for managing the SSE trace event stream connection.
 * Subscribes to debug:trace:{journeyGid} events via EventSource.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createTraceEventSource } from '../services/debugApi';

export interface TraceEvent {
  id: string;
  event: string;
  ts: string;
  stage?: string;
  source?: string;
  journey_gid?: string;
  data: Record<string, unknown>;
  raw: string;
}

export interface UseTraceStreamOptions {
  journeyGid: string;
  enabled: boolean;
  maxEvents?: number;
}

export interface UseTraceStreamResult {
  events: TraceEvent[];
  connected: boolean;
  error: string | null;
  clearEvents: () => void;
}

const DEFAULT_MAX_EVENTS = 500;

export function useTraceStream({
  journeyGid,
  enabled,
  maxEvents = DEFAULT_MAX_EVENTS,
}: UseTraceStreamOptions): UseTraceStreamResult {
  const [events, setEvents] = useState<TraceEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const eventCounterRef = useRef(0);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  useEffect(() => {
    if (!enabled || !journeyGid) {
      return;
    }

    let es: EventSource | null = null;

    const connect = () => {
      try {
        es = createTraceEventSource(journeyGid);
        eventSourceRef.current = es;

        es.onopen = () => {
          setConnected(true);
          setError(null);
        };

        // Handle 'checkpoint' events (pipeline stage data)
        es.addEventListener('checkpoint', (e: MessageEvent) => {
          try {
            const parsed = JSON.parse(e.data as string) as Record<string, unknown>;
            const traceEvent: TraceEvent = {
              id: String(++eventCounterRef.current),
              event: 'checkpoint',
              ts: (parsed.ts as string) ?? new Date().toISOString(),
              stage: parsed.stage as string | undefined,
              source: parsed.source as string | undefined,
              journey_gid: parsed.journey_gid as string | undefined,
              data: parsed,
              raw: e.data as string,
            };

            setEvents((prev) => {
              const next = [...prev, traceEvent];
              return next.length > maxEvents ? next.slice(next.length - maxEvents) : next;
            });
          } catch {
            // Non-fatal: skip malformed event
          }
        });

        // Handle 'trace_ended' event
        es.addEventListener('trace_ended', () => {
          setConnected(false);
          es?.close();
        });

        // Handle generic messages
        es.onmessage = (e: MessageEvent) => {
          try {
            const parsed = JSON.parse(e.data as string) as Record<string, unknown>;
            const traceEvent: TraceEvent = {
              id: String(++eventCounterRef.current),
              event: 'message',
              ts: new Date().toISOString(),
              data: parsed,
              raw: e.data as string,
            };
            setEvents((prev) => {
              const next = [...prev, traceEvent];
              return next.length > maxEvents ? next.slice(next.length - maxEvents) : next;
            });
          } catch {
            // Non-fatal: skip malformed event
          }
        };

        es.onerror = () => {
          setConnected(false);
          setError('Connection lost. Stream disconnected.');
          es?.close();
        };
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to connect');
        setConnected(false);
      }
    };

    connect();

    return () => {
      if (es) {
        es.close();
        eventSourceRef.current = null;
      }
      setConnected(false);
    };
  }, [enabled, journeyGid, maxEvents]);

  return { events, connected, error, clearEvents };
}
